import { TrackingHelper } from '@/utils/tracking';
import { default as dayjs } from 'dayjs';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { StatusCodes } from 'http-status-codes';
import { useCallback } from 'react';
import type {
  IntakeSession,
  IntakeSessionSource,
  UpdateSessionRequest,
} from '../types';

const SESSION_TTL_HOURS = 24;

const BASE_URL = `${process.env.NEXT_PUBLIC_MARKETING_API_URL}/v1/intake/state`;

export const createSession = async (
  source: IntakeSessionSource
): Promise<IntakeSession> => {
  const sessionId = crypto.randomUUID();

  const now = dayjs();

  const initialState: IntakeSession = {
    sessionId,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    expiresAt: now.add(SESSION_TTL_HOURS, 'hour').toISOString(),
    currentStepId: 'contact-info',
    completedSteps: [] as string[],
    formData: {},
    source,
    last_session_date: now.valueOf(),
    trackingData: TrackingHelper.getTrackingData(),
  };

  await fetch(`${BASE_URL}`, {
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    body: JSON.stringify({ sessionId, sessionData: initialState }),
  });

  return initialState;
};

export const fetchSession = async (
  sessionId: string
): Promise<IntakeSession | null> => {
  const response = await fetch(`${BASE_URL}/${encodeURIComponent(sessionId)}`);

  if (response.status === StatusCodes.NOT_FOUND) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to fetch session: ${response.status} ${errorData.error || response.statusText}`
    );
  }

  const data = await response.json();

  // API returns formData/completedSteps nested under sessionData, flatten for IntakeSession
  const sessionData = data.sessionData ?? {};

  return {
    sessionId: data.sessionId,
    createdAt: data.createdAt ?? '',
    updatedAt: data.updatedAt ?? '',
    expiresAt: data.expiresAt ?? '',
    currentStepId: sessionData.currentStepId ?? '',
    completedSteps: sessionData.completedSteps ?? [],
    formData: sessionData.formData ?? {},
    leadId: sessionData.leadId,
    contactId: sessionData.contactId,
    accountId: sessionData.accountId,
    opportunityId: sessionData.opportunityId,
    source: data.source ?? 'intake_v2',
    trackingData: sessionData.trackingData,
    last_session_date: sessionData.last_session_date,
  };
};

const updateSessionRequest = async ({
  sessionId,
  sessionData,
}: TrackStateChangeV2Request) => {
  try {
    const response = await fetch(`${BASE_URL}`, {
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        sessionData: {
          ...sessionData,
          last_session_date: Date.now(),
          trackingData: TrackingHelper.getTrackingData(),
        },
      }),
    });

    return response.json();
  } catch (error) {
    throw new Error(`Failed to update session: ${error};`);
  }
};

export const useUpdateSession = () => {
  const { isPending, mutateAsync } = useStateChangeTrackingV2Mutation();

  const update = useCallback(
    async (sessionId: string, updates: UpdateSessionRequest) => {
      const data = await mutateAsync({
        sessionId,
        sessionData: updates,
      });

      if (!data) {
        throw new Error('Failed to update session, received false-positive.');
      }
    },
    [mutateAsync]
  );

  return { update, loading: isPending };
};

type TrackStateChangeV2Response = {
  sessionId: string;
};

type TrackStateChangeV2Request = {
  // TODO -- update those
  sessionId: string;
  sessionData: UpdateSessionRequest;
};

const TRACK_STATE_CHANGE_KEY = ['state', 'track-change-v2'] as const;

export const useStateChangeTrackingV2Mutation = (
  options?: Omit<
    UseMutationOptions<
      TrackStateChangeV2Response,
      Error,
      TrackStateChangeV2Request
    >,
    'mutationFn'
  >
) =>
  useMutation<TrackStateChangeV2Response, Error, TrackStateChangeV2Request>({
    mutationKey: TRACK_STATE_CHANGE_KEY,
    mutationFn: updateSessionRequest,
    ...options,
  });
